import React, { useState, useEffect, useRef } from 'react';
import { Save, Download, Plus, Trash2, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { handlePrintToPDF } from "../utils/prints";


const GratuityNominationForm = ({eid, data}) => {
  const { user, logout } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [applicationId, setApplicationId] = useState(null);
  // check user exist or not
  useEffect(() => {
    if (!user) {
      logout();
    }
  }, []);
  const steps = [
    { id: 1, title: 'Establishment Details', icon: '🏢' },
    { id: 2, title: 'Employee Information', icon: '👤' },
    { id: 3, title: 'Address Details', icon: '📍' },
    { id: 4, title: 'Nominees', icon: '👥' },
    { id: 5, title: 'Declarations', icon: '📋' },
    { id: 6, title: 'Witnesses', icon: '✍️' },
    { id: 7, title: 'Review & Submit', icon: '✅' }
  ];

  const [formData, setFormData] = useState({
    establishmentName: '',
    establishmentAddress: '',
    employeeName: '',
    gender: '',
    religion: '',
    maritalStatus: '',
    department: '',
    position: '',
    appointmentDate: '',
    permanentAddress: {
      village: '',
      thana: '',
      postOffice: '',
      district: '',
      state: ''
    },
    nominees: [
      {
        name: '',
        address: '',
        relationship: '',
        age: '',
        proportion: ''
      }
    ],
    declarations: {
      familyMember: false,
      noFamily: false,
      parentsNotDependent: false,
      spouseParentsNotDependent: false,
      husbandExcluded: false,
      husbandExclusionDate: ''
    },
    witnesses: [
      { name: '', address: '' },
      { name: '', address: '' }
    ]
  });

  const [errors, setErrors] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');

  // Auto-save functionality
  useEffect(() => {
    const savedData = localStorage.getItem('gratuityFormData');
    const savedStep = localStorage.getItem('gratuityFormCurrentStep');
    const savedCompletedSteps = localStorage.getItem('gratuityFormCompletedSteps');
    
    if (savedData) {
      try {
        setFormData(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
    
    if (savedStep) {
      setCurrentStep(parseInt(savedStep));
    }
    
    if (savedCompletedSteps) {
      try {
        setCompletedSteps(JSON.parse(savedCompletedSteps));
      } catch (error) {
        console.error('Error loading completed steps:', error);
      }
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      localStorage.setItem('gratuityFormData', JSON.stringify(formData));
      localStorage.setItem('gratuityFormCurrentStep', currentStep.toString());
      localStorage.setItem('gratuityFormCompletedSteps', JSON.stringify(completedSteps));
      setLastSaved(new Date().toLocaleTimeString());
      setSaveStatus('saved');
    }, 1000);

    setSaveStatus('saving');
    return () => clearTimeout(timeoutId);
  }, [formData, currentStep, completedSteps]);

  const handleInputChange = (field, value, index = null, subField = null) => {
    setFormData(prev => {
      if (field === 'permanentAddress') {
        return {
          ...prev,
          permanentAddress: { ...prev.permanentAddress, [subField]: value }
        };
      }
      
      if (field === 'nominees') {
        const newNominees = [...prev.nominees];
        newNominees[index][subField] = value;
        return { ...prev, nominees: newNominees };
      }
      
      if (field === 'witnesses') {
        const newWitnesses = [...prev.witnesses];
        newWitnesses[index][subField] = value;
        return { ...prev, witnesses: newWitnesses };
      }
      
      if (field === 'declarations') {
        return {
          ...prev,
          declarations: { ...prev.declarations, [subField]: value }
        };
      }
      
      return { ...prev, [field]: value };
    });
  };

  const addNominee = () => {
    setFormData(prev => ({
      ...prev,
      nominees: [
        ...prev.nominees,
        { name: '', address: '', relationship: '', age: '', proportion: '' }
      ]
    }));
  };

  const removeNominee = (index) => {
    setFormData(prev => ({
      ...prev,
      nominees: prev.nominees.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.establishmentName.trim()) newErrors.establishmentName = 'Establishment name is required';
        break;
        
      case 2:
        if (!formData.employeeName.trim()) newErrors.employeeName = 'Employee name is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
        if (!formData.department.trim()) newErrors.department = 'Department is required';
        if (!formData.appointmentDate) newErrors.appointmentDate = 'Appointment date is required';
        break;
        
      case 4:
        if (formData.nominees.length === 0) {
          newErrors.nominees = 'At least one nominee is required';
        } else {
          formData.nominees.forEach((nominee, index) => {
            if (!nominee.name.trim()) newErrors[`nominee_${index}_name`] = 'Nominee name is required';
            if (!nominee.relationship.trim()) newErrors[`nominee_${index}_relationship`] = 'Relationship is required';
            if (!nominee.age || nominee.age < 1) newErrors[`nominee_${index}_age`] = 'Valid age is required';
            if (!nominee.proportion || nominee.proportion < 1) newErrors[`nominee_${index}_proportion`] = 'Valid proportion is required';
          });
          
          const totalProportion = formData.nominees.reduce((sum, nominee) => sum + (parseFloat(nominee.proportion) || 0), 0);
          if (totalProportion !== 100) {
            newErrors.totalProportion = 'Total proportion must equal 100%';
          }
        }
        break;
        
      case 6:
        formData.witnesses.forEach((witness, index) => {
          if (!witness.name.trim()) newErrors[`witness_${index}_name`] = 'Witness name is required';
          if (!witness.address.trim()) newErrors[`witness_${index}_address`] = 'Witness address is required';
        });
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      if (currentStep < steps.length) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  const generateApplicationId = () => {
    const prefix = 'GRT';
    const year = new Date().getFullYear();
    const month = String(new Date().getMonth() + 1).padStart(2, '0');
    const randomNum = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
    return `${prefix}${year}${month}${randomNum}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all steps
    let allValid = true;
    for (let step = 1; step <= 6; step++) {
      if (!validateStep(step)) {
        allValid = false;
        break;
      }
    }
    
    if (allValid) {
      const appId = generateApplicationId();
      setApplicationId(appId);
      
      const jsonData = {
        eid: !eid ? user.eid : eid,
        applicationId: appId,
        formType: 'Form F',
        submissionDate: new Date().toISOString(),
        status: 'Submitted',
        data: formData
      };
      
      await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/save-application`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${user.token}`
        },
        body: JSON.stringify(jsonData)
      });
      
      // Download JSON file
      // const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `gratuity_nomination_${appId}.json`;
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);
      // URL.revokeObjectURL(url);
      
      // Clear saved data
      localStorage.removeItem('gratuityFormData');
      localStorage.removeItem('gratuityFormCurrentStep');
      localStorage.removeItem('gratuityFormCompletedSteps');
      
      setCurrentStep(7);
    } else {
      alert('Please complete all required fields before submitting.');
    }
  };

  const clearForm = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      localStorage.removeItem('gratuityFormData');
      localStorage.removeItem('gratuityFormCurrentStep');
      localStorage.removeItem('gratuityFormCompletedSteps');
      
      setFormData({
        establishmentName: '',
        establishmentAddress: '',
        employeeName: '',
        gender: '',
        religion: '',
        maritalStatus: '',
        department: '',
        position: '',
        appointmentDate: '',
        permanentAddress: { village: '', thana: '', postOffice: '', district: '', state: '' },
        nominees: [{ name: '', address: '', relationship: '', age: '', proportion: '' }],
        declarations: { familyMember: false, noFamily: false, parentsNotDependent: false, spouseParentsNotDependent: false, husbandExcluded: false, husbandExclusionDate: '' },
        witnesses: [{ name: '', address: '' }, { name: '', address: '' }]
      });
      
      setErrors({});
      setCurrentStep(1);
      setCompletedSteps([]);
      setApplicationId(null);
    }
  };

  const getEmployeeData = async (eid) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/get-application?eid=${eid}&formType=Personal`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  const extractEmployeeCore = (finalData) => {
    if (!finalData) return null;
    let data = finalData.data;

    if (!data) return null;
    return {
        establishmentName: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",     // or full_name if you prefer
            gender: data.formData.gender || "",
            religion: data.formData.religion || "",
            maritalStatus: data.formData.maritalStatus || "",
            department: data.formData.department || "",
            postHeld: data.formData.postHeld || "",
            dateOfAppointment: data.formData.dateOfAppointment || "",
            permanentAddress: data.formData.permanentAddress || "",
            village: data.formData.village || "",
            thana: data.formData.thana || "",
            postOffice: data.formData.postOffice || "",
            district: data.formData.district || "",
            state: data.formData.state || ""
    };
  }

  useEffect(() => {
    if (!user) return;
    if(data){
      setFormData(data);
      return;
    }
    getEmployeeData(user.eid).then(data => setFormData(prev => ({...prev, ...extractEmployeeCore(data)}))).catch(error => console.error(error));
  }, []);


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name or Description of Establishment with Full Address *
              </label>
              <input
                type="text"
                value={formData.establishmentName}
                onChange={(e) => handleInputChange('establishmentName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.establishmentName ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter establishment name"
              />
              {errors.establishmentName && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {errors.establishmentName}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Establishment Address
              </label>
              <textarea
                value={formData.establishmentAddress}
                onChange={(e) => handleInputChange('establishmentAddress', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter full address of establishment"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Name (in full) *
                </label>
                <input
                  type="text"
                  value={formData.employeeName}
                  onChange={(e) => handleInputChange('employeeName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.employeeName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter full name"
                />
                {errors.employeeName && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors.employeeName}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.gender ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors.gender}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                <input
                  type="text"
                  value={formData.religion}
                  onChange={(e) => handleInputChange('religion', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter religion"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marital Status *
                </label>
                <select
                  value={formData.maritalStatus}
                  onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.maritalStatus ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select</option>
                  <option value="Unmarried">Unmarried</option>
                  <option value="Married">Married</option>
                  <option value="Widow">Widow</option>
                  <option value="Widower">Widower</option>
                </select>
                {errors.maritalStatus && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors.maritalStatus}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department/Branch/Section *
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.department ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter department"
                />
                {errors.department && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {errors.department}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Post Held with Ticket/Serial No.
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter position"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Appointment *
              </label>
              <input
                type="date"
                value={formData.appointmentDate}
                onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                className={`w-full max-w-xs px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.appointmentDate ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.appointmentDate && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {errors.appointmentDate}
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Permanent Address</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Village</label>
                <input
                  type="text"
                  value={formData.permanentAddress.village}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value, null, 'village')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter village"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thana</label>
                <input
                  type="text"
                  value={formData.permanentAddress.thana}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value, null, 'thana')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter thana"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Post Office</label>
                <input
                  type="text"
                  value={formData.permanentAddress.postOffice}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value, null, 'postOffice')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter post office"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <input
                  type="text"
                  value={formData.permanentAddress.district}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value, null, 'district')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter district"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.permanentAddress.state}
                  onChange={(e) => handleInputChange('permanentAddress', e.target.value, null, 'state')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter state"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Nominees</h3>
              <button
                type="button"
                onClick={addNominee}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Nominee
              </button>
            </div>
            
            {errors.nominees && (
              <p className="text-red-500 text-sm mb-4 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.nominees}
              </p>
            )}
            
            {errors.totalProportion && (
              <p className="text-red-500 text-sm mb-4 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.totalProportion}
              </p>
            )}
            
            {formData.nominees.map((nominee, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 last:mb-0">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-md font-medium text-gray-900">Nominee {index + 1}</h4>
                  {formData.nominees.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNominee(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name in Full with Address *
                    </label>
                    <textarea
                      value={nominee.name}
                      onChange={(e) => handleInputChange('nominees', e.target.value, index, 'name')}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`nominee_${index}_name`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter nominee name and full address"
                    />
                    {errors[`nominee_${index}_name`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`nominee_${index}_name`]}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship with Employee *
                    </label>
                    <input
                      type="text"
                      value={nominee.relationship}
                      onChange={(e) => handleInputChange('nominees', e.target.value, index, 'relationship')}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`nominee_${index}_relationship`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="e.g., Son, Daughter, Spouse"
                    />
                    {errors[`nominee_${index}_relationship`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`nominee_${index}_relationship`]}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age of Nominee *
                    </label>
                    <input
                      type="number"
                      value={nominee.age}
                      onChange={(e) => handleInputChange('nominees', e.target.value, index, 'age')}
                      min="1"
                      max="120"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`nominee_${index}_age`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Age"
                    />
                    {errors[`nominee_${index}_age`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`nominee_${index}_age`]}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proportion (%) *
                    </label>
                    <input
                      type="number"
                      value={nominee.proportion}
                      onChange={(e) => handleInputChange('nominees', e.target.value, index, 'proportion')}
                      min="1"
                      max="100"
                      step="0.01"
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`nominee_${index}_proportion`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Percentage share"
                    />
                    {errors[`nominee_${index}_proportion`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`nominee_${index}_proportion`]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Total Proportion: </strong>
                {formData.nominees.reduce((sum, nominee) => sum + (parseFloat(nominee.proportion) || 0), 0).toFixed(2)}%
                (Must equal 100%)
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Declarations</h3>
            
            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.declarations.familyMember}
                  onChange={(e) => handleInputChange('declarations', e.target.checked, null, 'familyMember')}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  I hereby certify that the person(s) mentioned is a/are member(s) of my family within the meaning of clause (h) of section (2) of the Payment of Gratuity Act, 1972.
                </span>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.declarations.noFamily}
                  onChange={(e) => handleInputChange('declarations', e.target.checked, null, 'noFamily')}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  I hereby declare that I have no family within the meaning of clause (h) of section (2) of the said Act.
                </span>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.declarations.parentsNotDependent}
                  onChange={(e) => handleInputChange('declarations', e.target.checked, null, 'parentsNotDependent')}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  My father/mother/parents is/are not dependent on me.
                </span>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.declarations.spouseParentsNotDependent}
                  onChange={(e) => handleInputChange('declarations', e.target.checked, null, 'spouseParentsNotDependent')}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  My husband's/wife's father/mother/parents is/are not dependent on my husband/wife.
                </span>
              </label>
              
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.declarations.husbandExcluded}
                  onChange={(e) => handleInputChange('declarations', e.target.checked, null, 'husbandExcluded')}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <span className="text-sm text-gray-700 block mb-2">
                    I have excluded my husband from my family by a notice dated:
                  </span>
                  {formData.declarations.husbandExcluded && (
                    <input
                      type="date"
                      value={formData.declarations.husbandExclusionDate}
                      onChange={(e) => handleInputChange('declarations', e.target.value, null, 'husbandExclusionDate')}
                      className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Witnesses</h3>
            
            {formData.witnesses.map((witness, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 last:mb-0">
                <h4 className="text-md font-medium text-gray-900 mb-3">Witness {index + 1}</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name in Full *
                    </label>
                    <input
                      type="text"
                      value={witness.name}
                      onChange={(e) => handleInputChange('witnesses', e.target.value, index, 'name')}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`witness_${index}_name`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter witness name"
                    />
                    {errors[`witness_${index}_name`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`witness_${index}_name`]}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Address *
                    </label>
                    <textarea
                      value={witness.address}
                      onChange={(e) => handleInputChange('witnesses', e.target.value, index, 'address')}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[`witness_${index}_address`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter witness full address"
                    />
                    {errors[`witness_${index}_address`] && (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors[`witness_${index}_address`]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 7:
        if (applicationId) {
          return (
            <div className="text-center space-y-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Form Submitted Successfully!</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800 mb-2">Your Application ID:</p>
                  <p className="text-xl font-mono font-bold text-green-900">{applicationId}</p>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Please save this Application ID for your records. Your form data has been downloaded as a JSON file.
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={clearForm}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Submit New Application
                  </button>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Review & Submit</h3>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Please Review Your Information</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Once submitted, you will receive an Application ID and your form data will be downloaded as a JSON file.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Establishment Details</h4>
                  <p className="text-sm text-gray-700">{formData.establishmentName || 'Not provided'}</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Employee Information</h4>
                  <p className="text-sm text-gray-700">
                    {formData.employeeName || 'Not provided'} - {formData.department || 'Department not provided'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Appointment Date: {formData.appointmentDate || 'Not provided'}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Nominees ({formData.nominees.length})</h4>
                  {formData.nominees.map((nominee, index) => (
                    <div key={index} className="text-sm text-gray-700 mb-1">
                      {nominee.name || 'Name not provided'} - {nominee.relationship || 'Relationship not provided'} ({nominee.proportion || '0'}%)
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-lg rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 text-center">
              FORM 'F' - GRATUITY NOMINATION
            </h1>
            <p className="text-sm text-gray-600 text-center mt-1">
              Payment of Gratuity Act, 1972
            </p>
            
            {/* Auto-save status */}
            <div className="flex items-center justify-center mt-3 text-sm">
              {saveStatus === 'saving' ? (
                <div className="flex items-center text-yellow-600">
                  <Save className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </div>
              ) : (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Last saved: {lastSaved || 'Never'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step Navigation */}
        <div className="bg-white shadow-lg rounded-lg mb-6 p-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => goToStep(step.id)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-all ${
                    currentStep === step.id
                      ? 'bg-blue-600 text-white'
                      : completedSteps.includes(step.id)
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {completedSteps.includes(step.id) && currentStep !== step.id ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    step.icon
                  )}
                </button>
                <div className="ml-2 hidden sm:block">
                  <p className={`text-xs font-medium ${
                    currentStep === step.id ? 'text-blue-600' : 'text-gray-600'
                  }`}>
                    Step {step.id}
                  </p>
                  <p className={`text-xs ${
                    currentStep === step.id ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    completedSteps.includes(step.id) ? 'bg-green-300' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {steps[currentStep - 1]?.title}
            </h2>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            
            {/* Navigation Buttons */}
            {currentStep < 7 && (
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentStep === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                
                <div className="text-sm text-gray-500">
                  Step {currentStep} of {steps.length}
                </div>
                
                {currentStep === 6 ? (
                  <button
                    type="submit"
                    className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Submit Application
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                )}
              </div>
            )}
            
            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">
                      Please fix the following errors:
                    </h3>
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                      {Object.values(errors).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="bg-white shadow-lg rounded-lg p-4">
          <p className="text-center text-sm text-gray-600">
            This form is based on Form 'F' under the Payment of Gratuity Act, 1972
          </p>
        </div>
      </div>
    </div>
  );
};

export default GratuityNominationForm;




export const FormF = ({eid, data}) => {
  const { user, logout, checkAuthStatus } = useAuth();
  const printRef = useRef();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!user) {
      logout();
    }
  }, []);

  const [formData, setFormData] = useState({
    establishmentName: '',
    employeeName: '',
    nominees: [
      { name: '', relationship: '', age: '', proportion: '' },
      { name: '', relationship: '', age: '', proportion: '' },
      { name: '', relationship: '', age: '', proportion: '' },
      { name: '', relationship: '', age: '', proportion: '' },
      { name: '', relationship: '', age: '', proportion: '' }
    ],
    certifications: {
      familyMember: false,
      noFamily: false,
      parentsNotDependent: false,
      husbandsParentsNotDependent: false,
      husbandExcluded: false,
      husbandExclusionDate: '',
      invalidatesPrevious: false
    },
    employeeDetails: {
      fullName: '',
      gender: '',
      religion: '',
      maritalStatus: '',
      department: '',
      postHeld: '',
      dateOfAppointment: '',
      permanentAddress: '',
      village: '',
      thana: '',
      postOffice: '',
      district: '',
      state: ''
    },
    witnesses: [
      { name: '', address: '' },
      { name: '', address: '' }
    ],
    place: '',
    date: '',
    employeeSignature: null,
    witness1Signature: null,
    witness2Signature: null,
    employerSignature: null,
    employerStamp: null,
    acknowledgementSignature: null,
    employerReference: '',
    employerDate: '',
    acknowledgementDate: ''
  });

  const [errors, setErrors] = useState([]);
  const [signaturePreviews, setSignaturePreviews] = useState({
    employeeSignature: null,
    witness1Signature: null,
    witness2Signature: null,
    employerSignature: null,
    employerStamp: null,
    acknowledgementSignature: null
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNomineeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      nominees: prev.nominees.map((nominee, i) => 
        i === index ? { ...nominee, [field]: value } : nominee
      )
    }));
  };

  const handleWitnessChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      witnesses: prev.witnesses.map((witness, i) => 
        i === index ? { ...witness, [field]: value } : witness
      )
    }));
  };
  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;
  const handleFileUpload = async (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Create filename with eid prefix
      const serverFilename = `${!eid ? user.eid : eid}_${docType}`;
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Rename the file before uploading
      const renamedFile = new File([file], serverFilename, { type: file.type });
      formData.append('file', renamedFile);

      // Upload to API
      const response = await fetch(`${API_BASE_URL}?eid=${!eid ? user.eid : eid}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Refresh the documents list to get the new URL
      // await fetchUploadedDocuments();

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      event.target.value = ''; // Reset file input
    }
  };

  const handleSignatureUpload = async (e, signatureType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (100KB = 102400 bytes)
    if (file.size > 102400) {
      alert('Signature file size must be less than 100KB. Please upload a smaller file.');
      e.target.value = '';
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSignaturePreviews(prev => ({
        ...prev,
        [signatureType]: reader.result
      }));
      setFormData(prev => ({
        ...prev,
        [signatureType]: true
      }));
    };
    reader.readAsDataURL(file);
    await handleFileUpload(signatureType, e);
  };
  const getDocsUrls = async (eid)=> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-unused-urls?eid=${eid}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const urlsData = await response.json();
      console.log(urlsData);
      return urlsData.urls;
    } catch (error) {
      console.error('Error fetching documents:', error);
      return null;
    }
  }

  const fetchAndSetSignatures = async (eid) => {
    try {
      const urls = await getDocsUrls(eid ? eid : user.eid); // returns an array of URLs
  
      // Define mapping between keyword and state key
      const signatureMap = {
        employeeSignature: "employeeSignature",
        witness1Signature: "witness1Signature",
        witness2Signature: "witness2Signature",
        employerSignature: "employerSignature",
        employerStamp: "employerStamp",
        acknowledgementSignature: "acknowledgementSignature"
      };
  
      // Loop through each signature type and try to find a matching URL
      Object.entries(signatureMap).forEach(async ([key, value]) => {
        const match = urls.find(url => url.toLowerCase().includes(key.toLowerCase()));
  
        if (match) {
          // Fetch and convert to Base64 Data URL
          const response = await fetch(match);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setSignaturePreviews(prev => ({ 
              ...prev,
              [value]: reader.result
            }));
            setFormData(prev => ({
              ...prev,
              [value]: true
            }));
          };
          reader.readAsDataURL(blob);
        }
      });
    } catch (error) {
      console.error("Error loading signature previews:", error);
    }
  };
  const removeSignature = (signatureType) => {
    setSignaturePreviews(prev => ({
      ...prev,
      [signatureType]: null
    }));
    setFormData(prev => ({
      ...prev,
      [signatureType]: null
    }));
  };

  const validateForm = () => {
    const validationErrors = [];

    // Establishment Name
    if (!formData.establishmentName.trim()) {
      validationErrors.push('Establishment Name is required');
    }

    // Employee Name
    if (!formData.employeeName.trim()) {
      validationErrors.push('Employee Name is required');
    }

    // At least one nominee must be filled
    const hasNominee = formData.nominees.some(nominee => 
      nominee.name.trim() && nominee.relationship.trim() && nominee.age && nominee.proportion.trim()
    );
    if (!hasNominee) {
      validationErrors.push('At least one complete Nominee entry is required (Name, Relationship, Age, and Proportion)');
    }

    // Validate filled nominees have all fields
    formData.nominees.forEach((nominee, index) => {
      const hasAnyField = nominee.name || nominee.relationship || nominee.age || nominee.proportion;
      if (hasAnyField) {
        if (!nominee.name.trim()) validationErrors.push(`Nominee ${index + 1}: Name is required`);
        if (!nominee.relationship.trim()) validationErrors.push(`Nominee ${index + 1}: Relationship is required`);
        if (!nominee.age) validationErrors.push(`Nominee ${index + 1}: Age is required`);
        if (!nominee.proportion.trim()) validationErrors.push(`Nominee ${index + 1}: Proportion is required`);
      }
    });

    // At least one certification must be checked
    const hasCertification = Object.values(formData.certifications).some(val => val === true);
    if (!hasCertification) {
      validationErrors.push('At least one certification (items 2-6) must be selected');
    }

    // Employee Details - all fields mandatory
    if (!formData.employeeDetails.fullName.trim()) validationErrors.push('Employee Full Name is required');
    if (!formData.employeeDetails.gender) validationErrors.push('Gender is required');
    if (!formData.employeeDetails.religion.trim()) validationErrors.push('Religion is required');
    if (!formData.employeeDetails.maritalStatus) validationErrors.push('Marital Status is required');
    if (!formData.employeeDetails.department.trim()) validationErrors.push('Department is required');
    if (!formData.employeeDetails.postHeld.trim()) validationErrors.push('Post Held is required');
    if (!formData.employeeDetails.dateOfAppointment) validationErrors.push('Date of Appointment is required');
    if (!formData.employeeDetails.permanentAddress.trim()) validationErrors.push('Permanent Address is required');
    if (!formData.employeeDetails.village.trim()) validationErrors.push('Village is required');
    if (!formData.employeeDetails.thana.trim()) validationErrors.push('Thana is required');
    if (!formData.employeeDetails.postOffice.trim()) validationErrors.push('Post Office is required');
    if (!formData.employeeDetails.district.trim()) validationErrors.push('District is required');
    if (!formData.employeeDetails.state.trim()) validationErrors.push('State is required');

    // Place and Date
    if (!formData.place.trim()) validationErrors.push('Place is required');
    if (!formData.date) validationErrors.push('Date is required');

    // Employee Signature
    if (!formData.employeeSignature) validationErrors.push('Employee Signature is required');

    // Witnesses - both must be filled
    formData.witnesses.forEach((witness, index) => {
      if (!witness.name.trim()) validationErrors.push(`Witness ${index + 1}: Name is required`);
      if (!witness.address.trim()) validationErrors.push(`Witness ${index + 1}: Address is required`);
    });

    // Witness Signatures
    // if (!formData.witness1Signature) validationErrors.push('Witness 1 Signature is required');
    // if (!formData.witness2Signature) validationErrors.push('Witness 2 Signature is required');

    // Employer Signature and Stamp
    // if (!formData.employerSignature) validationErrors.push('Employer Signature is required');
    // if (!formData.employerStamp) validationErrors.push('Employer Stamp is required');

    // Acknowledgement Signature
    // if (!formData.acknowledgementSignature) validationErrors.push('Acknowledgement Signature is required');

    return validationErrors;
  };

  const handleSubmit = async (e, submitType="Final") => {
    e.preventDefault();
    try {
      const validationErrors = validateForm();
    
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setErrors([]);

      const finalData = {
        eid: !eid ? user.eid : eid, 
        applicationId: `KCL${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`, 
        formType: "Form F", 
        submissionDate: new Date().toISOString(), 
        data: formData, 
        submitType 
      };
    
      await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/save-application`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("access_token")}`
        },
        body: JSON.stringify(finalData),
      });
      checkAuthStatus();
      navigate("/");
    } catch (error) {
      console.error(error);
    }
  };

  const getEmployeeData = async (eid, formType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/get-application?eid=${eid}&formType=${formType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  const extractEmployeeCore = (finalData) => {
    if (!finalData) return null;
    let data = finalData.data;

    if (!data) return null;
    return {
        employeeName: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",
        employeeDetails: {
            fullName: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",
            gender: data.formData.gender || "",
            religion: data.formData.religion || "",
            maritalStatus: data.formData.maritalStatus || "",
            department: data.formData.department || "",
            postHeld: data.formData.postHeld || "",
            dateOfAppointment: data.formData.dateOfAppointment || "",
            permanentAddress: data.formData.permanentAddress || "",
            village: data.formData.village || "",
            thana: data.formData.thana || "",
            postOffice: data.formData.postOffice || "",
            district: data.formData.district || "",
            state: data.formData.state || ""
        }
    };
  }
      const [isSubmitted, setIsSubmitted] = useState(false);
      const fetchEmployeeData = async () => {
        try {
          // First try Joining API
          const joiningData = await getEmployeeData(user.eid, "Form F");
      
          if (joiningData?.data) {
            setFormData(joiningData.data);
            setIsSubmitted(joiningData.approved);
          } else {
            // If Joining data is empty, try Personal API
            const personalData = await getEmployeeData(user.eid, "Personal");
            setFormData(prev => ({
              ...prev,
              ...extractEmployeeCore(personalData),
            }));
          }
      
        } catch (error) {
          // If Joining API failed (e.g. 404), fallback to Personal
          try {
            const personalData = await getEmployeeData(user.eid, "Personal");
            setFormData(prev => ({
              ...prev,
              ...extractEmployeeCore(personalData),
            }));
          } catch (err2) {
            console.error("Both API calls failed:", err2);
          }
        }
      };
  useEffect(() => {
    if (!user) return;
    if(data){
      setFormData(data);
      fetchAndSetSignatures(eid?eid:user.eid);

      // Set all signature previews if they exist in data
      // const previews = {};
      // ['employeeSignature', 'witness1Signature', 'witness2Signature', 'employerSignature', 'employerStamp', 'acknowledgementSignature'].forEach(sig => {
      //   if (data[sig]) {
      //     previews[sig] = data[sig];
      //   }
      // });
      // setSignaturePreviews(prev => ({...prev, ...previews}));
      return;
    }
    fetchAndSetSignatures(eid?eid:user.eid);
    fetchEmployeeData();
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white" ref={printRef}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-3 rounded mb-6">
            <h3 className="font-bold mb-2">Please fix the following errors:</h3>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-sm">{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold underline mb-6">FORM 'F'</h1>
        </div>

        {/* Establishment Name */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            To: (Give here name or description of the establishment with full address) <span className="text-red-600">*</span>
          </label>
          <textarea
            className="w-full p-2 border border-gray-300 rounded"
            rows="2"
            disabled={isSubmitted}
            value={formData.establishmentName}
            onChange={(e) => handleInputChange('establishmentName', e.target.value)}
            required
          />
        </div>

        {/* Main Declaration */}
        <div className="space-y-4">
          <div className="flex items-start space-x-2">
            <span className="font-medium">1.</span>
            <div className="flex-1">
              <p className="mb-2">
                I, Shri/Smt./Kum. <input 
                  type="text" 
                  disabled={isSubmitted}
                  className="inline-block w-60 px-2 py-1 border-b border-gray-300 mx-2"
                  value={formData.employeeName}
                  onChange={(e) => handleInputChange('employeeName', e.target.value)}
                  required
                /> <span className="text-red-600">*</span> whose particulars are given in the statement below, hereby nominate the person(s) mentioned below to receive the gratuity payable after my death as also the gratuity standing to my credit in the event of my death before that amount has become payable, or having become payable has not been paid and direct that the said amount of gratuity shall be paid in proportion indicated against the name(s) of the nominee(s).
              </p>
            </div>
          </div>

          {/* Certifications */}
          <div className="space-y-3">
            <p className="text-sm text-red-600 font-medium">* At least one certification must be selected</p>
            <div className="flex items-start space-x-2">
              <span className="font-medium">2.</span>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  disabled={isSubmitted}
                  checked={formData.certifications.familyMember}
                  onChange={(e) => handleNestedChange('certifications', 'familyMember', e.target.checked)}
                />
                <span>I hereby certify that the person(s) mentioned is a /are member(s) of my family within the meaning of clause (h) of section (2) of the Payment of Gratuity Act, 1972.</span>
              </label>
            </div>

            <div className="flex items-start space-x-2">
              <span className="font-medium">3.</span>
              <label className="flex items-center space-x-2">
                <input
                disabled={isSubmitted}
                  type="checkbox"
                  checked={formData.certifications.noFamily}
                  onChange={(e) => handleNestedChange('certifications', 'noFamily', e.target.checked)}
                />
                <span>I hereby declare that I have no family within the meaning of clause (h) of section (2) of the said Act.</span>
              </label>
            </div>

            <div className="flex items-start space-x-2">
              <span className="font-medium">4.</span>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <span>(a)</span>
                  <input
                    type="checkbox"
                    disabled={isSubmitted}
                    checked={formData.certifications.parentsNotDependent}
                    onChange={(e) => handleNestedChange('certifications', 'parentsNotDependent', e.target.checked)}
                  />
                  <span>My father / mother/ parents is/are not dependant on me.</span>
                </label>
                <label className="flex items-center space-x-2">
                  <span>(b)</span>
                  <input
                    type="checkbox"
                    disabled={isSubmitted}
                    checked={formData.certifications.husbandsParentsNotDependent}
                    onChange={(e) => handleNestedChange('certifications', 'husbandsParentsNotDependent', e.target.checked)}
                  />
                  <span>My husband's father/mother/parents is/are not dependant on my husband.</span>
                </label>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <span className="font-medium">5.</span>
              <div className="flex items-center space-x-2 flex-wrap">
                <input
                  type="checkbox"
                  disabled={isSubmitted}
                  checked={formData.certifications.husbandExcluded}
                  onChange={(e) => handleNestedChange('certifications', 'husbandExcluded', e.target.checked)}
                />
                <span>I have excluded my husband from my family by a notice dated</span>
                <input
                  type="date"
                  disabled={isSubmitted}
                  className="px-2 py-1 border border-gray-300 rounded"
                  value={formData.certifications.husbandExclusionDate}
                  onChange={(e) => handleNestedChange('certifications', 'husbandExclusionDate', e.target.value)}
                />
                <span>to the controlling Authority in terms of the proviso to clause (h) of section 2 of the said Act.</span>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <span className="font-medium">6.</span>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  disabled={isSubmitted}
                  checked={formData.certifications.invalidatesPrevious}
                  onChange={(e) => handleNestedChange('certifications', 'invalidatesPrevious', e.target.checked)}
                />
                <span>Nomination made herein invalidates my previous nomination.</span>
              </label>
            </div>
          </div>
        </div>

        {/* Nominees Table */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-center underline mb-4">Nominee(s) <span className="text-red-600">*</span></h3>
          <p className="text-sm text-red-600 mb-2">* At least one complete nominee entry is required</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-400">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-400 p-3 text-left">Name in full with full address of nominee(s)</th>
                  <th className="border border-gray-400 p-3 text-left">Relationship with the employee</th>
                  <th className="border border-gray-400 p-3 text-left">Age of nominee</th>
                  <th className="border border-gray-400 p-3 text-left">Proportion by which the gratuity will be shared</th>
                </tr>
              </thead>
              <tbody>
                {formData.nominees.map((nominee, index) => (
                  <tr key={index} >
                    <td className="border border-gray-400 p-2">
                      <textarea
                        className="w-full p-1 border-none resize-none"
                        rows="2"
                        disabled={isSubmitted}
                        value={nominee.name}
                        onChange={(e) => handleNomineeChange(index, 'name', e.target.value)}
                      />
                    </td>
                    <td className="border border-gray-400 p-2">
                      <input
                        type="text"
                        disabled={isSubmitted}
                        className="w-full p-1 border-none"
                        value={nominee.relationship}
                        onChange={(e) => handleNomineeChange(index, 'relationship', e.target.value)}
                      />
                    </td>
                    <td className="border border-gray-400 p-2">
                      <input
                        type="number"
                        disabled={isSubmitted}
                        className="w-full p-1 border-none"
                        value={nominee.age}
                        onChange={(e) => handleNomineeChange(index, 'age', e.target.value)}
                      />
                    </td>
                    <td className="border border-gray-400 p-2">
                      <input
                        type="text"
                        disabled={isSubmitted}
                        className="w-full p-1 border-none"
                        value={nominee.proportion}
                        onChange={(e) => handleNomineeChange(index, 'proportion', e.target.value)}
                        placeholder="e.g., 50% or 1/2"
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="border border-gray-400 p-2 text-center font-medium">So on</td>
                  <td className="border border-gray-400 p-2"></td>
                  <td className="border border-gray-400 p-2"></td>
                  <td className="border border-gray-400 p-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Statement Section */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-center underline mb-4">Statement</h3>
          <p className="text-sm text-red-600 mb-4">* All fields are required</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">1. Name of employee in full: <span className="text-red-600">*</span></label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.fullName}
                onChange={(e) => handleNestedChange('employeeDetails', 'fullName', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">2. Gender: <span className="text-red-600">*</span></label>
              <select
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.gender}
                onChange={(e) => handleNestedChange('employeeDetails', 'gender', e.target.value)}
                required
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">3. Religion: <span className="text-red-600">*</span></label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.religion}
                onChange={(e) => handleNestedChange('employeeDetails', 'religion', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">4. Whether unmarried/ married/ widow/ widower: <span className="text-red-600">*</span></label>
              <select
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.maritalStatus}
                onChange={(e) => handleNestedChange('employeeDetails', 'maritalStatus', e.target.value)}
                required
              >
                <option value="">Select</option>
                <option value="unmarried">Unmarried</option>
                <option value="married">Married</option>
                <option value="widow">Widow</option>
                <option value="widower">Widower</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">5. Department/ Branch/ Section where employed: <span className="text-red-600">*</span></label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.department}
                onChange={(e) => handleNestedChange('employeeDetails', 'department', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">6. Post held with Ticket or Serial No., if any: <span className="text-red-600">*</span></label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.postHeld}
                onChange={(e) => handleNestedChange('employeeDetails', 'postHeld', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">7. Date of Appointment: <span className="text-red-600">*</span></label>
              <input
                type="date"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.dateOfAppointment}
                onChange={(e) => handleNestedChange('employeeDetails', 'dateOfAppointment', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="mt-4 pdf-break">
            <label className="block text-sm font-medium mb-1">8. Permanent Address: <span className="text-red-600">*</span></label>
            <textarea
              className="w-full p-2 border border-gray-300 rounded mb-2"
              rows="2"
              value={formData.employeeDetails.permanentAddress}
              onChange={(e) => handleNestedChange('employeeDetails', 'permanentAddress', e.target.value)}
              required
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Village: <span className="text-red-600">*</span></label>
                <input
                  type="text"
                  disabled={isSubmitted}
                  className="w-full p-2 border border-gray-300 rounded"
                  value={formData.employeeDetails.village}
                  onChange={(e) => handleNestedChange('employeeDetails', 'village', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Thana: <span className="text-red-600">*</span></label>
                <input
                  type="text"
                  disabled={isSubmitted}
                  className="w-full p-2 border border-gray-300 rounded"
                  value={formData.employeeDetails.thana}
                  onChange={(e) => handleNestedChange('employeeDetails', 'thana', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Post Office: <span className="text-red-600">*</span></label>
                <input
                  type="text"
                  disabled={isSubmitted}
                  className="w-full p-2 border border-gray-300 rounded"
                  value={formData.employeeDetails.postOffice}
                  onChange={(e) => handleNestedChange('employeeDetails', 'postOffice', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">District: <span className="text-red-600">*</span></label>
                <input
                  type="text"
                  disabled={isSubmitted}
                  className="w-full p-2 border border-gray-300 rounded"
                  value={formData.employeeDetails.district}
                  onChange={(e) => handleNestedChange('employeeDetails', 'district', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="block text-sm font-medium mb-1">State: <span className="text-red-600">*</span></label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employeeDetails.state}
                onChange={(e) => handleNestedChange('employeeDetails', 'state', e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Place and Date */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Place: <span className="text-red-600">*</span></label>
            <input
              type="text"
              disabled={isSubmitted}
              className="w-full p-2 border border-gray-300 rounded"
              value={formData.place}
              onChange={(e) => handleInputChange('place', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Date: <span className="text-red-600">*</span></label>
            <input
              type="date"
              disabled={isSubmitted}
              className="w-full p-2 border border-gray-300 rounded"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              required
            />
          </div>
        </div>

        {/* Employee Signature */}
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2">
            Signature/ Thumb impression of the employee <span className="text-red-600">*</span>
          </label>
          <p className="text-xs text-gray-600 mb-2">Upload signature image (Max size: 100KB, Formats: PNG, JPG, JPEG)</p>
          
          {!signaturePreviews.employeeSignature ? (
            <div className="border-2 border-dashed border-gray-300 rounded p-4">
              <input
                type="file"
                disabled={isSubmitted}
                accept="image/*"
                onChange={(e) => handleSignatureUpload(e, 'employeeSignature')}
                className="hidden"
                id="employee-signature-upload"
              />
              <label
                htmlFor="employee-signature-upload"
                className="cursor-pointer inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
              >
                Upload Signature
              </label>
            </div>
          ) : (
            <div className="border border-gray-300 p-2">
              <div className="flex justify-between items-start mb-1">
                <p className="text-xs font-medium">Signature Preview:</p>
                <button
                  type="button"
                  onClick={() => removeSignature('employeeSignature')}
                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                >
                  Remove
                </button>
              </div>
              <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '80px' }}>
                <img
                  src={signaturePreviews.employeeSignature}
                  alt="Employee Signature"
                  className="max-h-20 max-w-full object-contain"
                />
              </div>
            </div>
          )}
        </div>

        {/* Declaration by Witnesses */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-center underline mb-4">Declaration by Witnesses</h3>
          <p className="text-center mb-4">Signature signed / thumb impressed before me.</p>
          <p className="text-sm text-red-600 mb-4">* Both witnesses are required</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Name in full and full address of witnesses:</h4>
              {formData.witnesses.map((witness, index) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm font-medium mb-1">{index + 1}. Name: <span className="text-red-600">*</span></label>
                  <input
                    type="text"
                    disabled={isSubmitted}
                    className="w-full p-2 border border-gray-300 rounded mb-2"
                    value={witness.name}
                    onChange={(e) => handleWitnessChange(index, 'name', e.target.value)}
                    required
                  />
                  <label className="block text-sm font-medium mb-1">Address: <span className="text-red-600">*</span></label>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded"
                    rows="2"
                    disabled={isSubmitted}
                    value={witness.address}
                    onChange={(e) => handleWitnessChange(index, 'address', e.target.value)}
                    required
                  />
                </div>
              ))}
            </div>
            <div>
              <h4 className="font-medium mb-2">Signature of Witnesses:</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm mb-2">1. Signature of Witness 1</p>
                  {!signaturePreviews.witness1Signature ? (
                    <div className="border-2 border-dashed border-gray-300 rounded p-3">
                      <input
                        type="file"
                        disabled={isSubmitted}
                        accept="image/*"
                        onChange={(e) => handleSignatureUpload(e, 'witness1Signature')}
                        className="hidden"
                        id="witness1-signature-upload"
                      />
                      <label
                        htmlFor="witness1-signature-upload"
                        className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                      >
                        Upload Signature
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-300 p-2">
                      <div className="flex justify-between items-start mb-1">
                        <button
                          type="button"
                          disabled={isSubmitted}
                          onClick={() => removeSignature('witness1Signature')}
                          className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '60px' }}>
                        <img
                          src={signaturePreviews.witness1Signature}
                          alt="Witness 1 Signature"
                          className="max-h-16 max-w-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <br />
                <div className="pdf-break">
                  <p className="text-sm mb-2">2. Signature of Witness 2</p>
                  {!signaturePreviews.witness2Signature ? (
                    <div className="border-2 border-dashed border-gray-300 rounded p-3">
                      <input
                        type="file"
                        disabled={isSubmitted}
                        accept="image/*"
                        onChange={(e) => handleSignatureUpload(e, 'witness2Signature')}
                        className="hidden"
                        id="witness2-signature-upload"
                      />
                      <label
                        htmlFor="witness2-signature-upload"
                        className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                      >
                        Upload Signature
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-300 p-2">
                      <div className="flex justify-between items-start mb-1">
                        <button
                          type="button"
                          disabled={isSubmitted}
                          onClick={() => removeSignature('witness2Signature')}
                          className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '60px' }}>
                        <img
                          src={signaturePreviews.witness2Signature}
                          alt="Witness 2 Signature"
                          className="max-h-16 max-w-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certificate by Employer */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-center underline mb-4">Certificate by the Employer</h3>
          <p className="mb-4">Certified that the particulars of the above nomination have been verified and recorded in this Company.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Employer's Reference No., if any:</label>
              <input
                type="text"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employerReference}
                onChange={(e) => handleInputChange('employerReference', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date:</label>
              <input
                type="date"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.employerDate}
                onChange={(e) => handleInputChange('employerDate', e.target.value)}
              />
            </div>
          </div>
          
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm mb-2 text-center">Signature of the employer / Officer authorised</p>
              <p className="text-sm mb-2 text-center">Designation</p>
              {!signaturePreviews.employerSignature ? (
                <div className="border-2 border-dashed border-gray-300 rounded p-3 text-center">
                  <input
                    type="file"
                    disabled={isSubmitted}
                    accept="image/*"
                    onChange={(e) => handleSignatureUpload(e, 'employerSignature')}
                    className="hidden"
                    id="employer-signature-upload"
                  />
                  <label
                    htmlFor="employer-signature-upload"
                    className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                  >
                    Upload Signature
                  </label>
                </div>
              ) : (
                <div className="border border-gray-300 p-2">
                  <div className="flex justify-between items-start mb-1">
                    <button
                      type="button"
                      disabled={isSubmitted}
                      onClick={() => removeSignature('employerSignature')}
                      className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '60px' }}>
                    <img
                      src={signaturePreviews.employerSignature}
                      alt="Employer Signature"
                      className="max-h-16 max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <p className="text-sm mb-2 text-center">Name and Address of the establishment or rubber stamp thereof.</p>
              {!signaturePreviews.employerStamp ? (
                <div className="border-2 border-dashed border-gray-300 rounded p-3 text-center">
                  <input
                    type="file"
                    disabled={isSubmitted}
                    accept="image/*"
                    onChange={(e) => handleSignatureUpload(e, 'employerStamp')}
                    className="hidden"
                    id="employer-stamp-upload"
                  />
                  <label
                    htmlFor="employer-stamp-upload"
                    className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                  >
                    Upload Stamp
                  </label>
                </div>
              ) : (
                <div className="border border-gray-300 p-2">
                  <div className="flex justify-between items-start mb-1">
                    <button
                      type="button"
                      disabled={isSubmitted}
                      onClick={() => removeSignature('employerStamp')}
                      className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '60px' }}>
                    <img
                      src={signaturePreviews.employerStamp}
                      alt="Employer Stamp"
                      className="max-h-16 max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Acknowledgement by Employer */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-center underline mb-4">Acknowledgement by the Employer</h3>
          <p className="mb-4">Received the duplicate copy of nomination in Form 'F' filed by me and duly certified by the Employer.</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Date:</label>
              <input
                type="date"
                disabled={isSubmitted}
                className="w-full p-2 border border-gray-300 rounded"
                value={formData.acknowledgementDate}
                onChange={(e) => handleInputChange('acknowledgementDate', e.target.value)}
              />
            </div>
            <div>
              <p className="text-sm mb-2">Signature of the Employee</p>
              {!signaturePreviews.acknowledgementSignature ? (
                <div className="border-2 border-dashed border-gray-300 rounded p-3">
                  <input
                    type="file"
                    disabled={isSubmitted}
                    accept="image/*"
                    onChange={(e) => handleSignatureUpload(e, 'acknowledgementSignature')}
                    className="hidden"
                    id="acknowledgement-signature-upload"
                  />
                  <label
                    htmlFor="acknowledgement-signature-upload"
                    className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                  >
                    Upload Signature
                  </label>
                </div>
              ) : (
                <div className="border border-gray-300 p-2">
                  <div className="flex justify-between items-start mb-1">
                    <button
                      type="button"
                      disabled={isSubmitted}
                      onClick={() => removeSignature('acknowledgementSignature')}
                      className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="bg-gray-50 p-2 flex justify-center items-center" style={{ minHeight: '60px' }}>
                    <img
                      src={signaturePreviews.acknowledgementSignature}
                      alt="Acknowledgement Signature"
                      className="max-h-16 max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="no-print mt-8 text-center">
          <button
            type="button"
            onClick={()=>handlePrintToPDF(printRef, formData.employeeName)}
            className="flex-1 mr-4 bg-gray-500 text-white py-3 px-6 rounded-md hover:bg-gray-600 transition duration-200 font-medium"
          >
            Print
          </button>
          <button
            type="submit"
            disabled={isSubmitted}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Submit Form
          </button>
        </div>
      </form>
    </div>
  );
};
